import React, { useState } from "react";
import { Button, Card, ErrorMessage, Flex, Link, Text } from "@jsq/ds/core";
import { Form, useFormContext } from "@jsq/ds/forms";
import { DefaultModal } from "../common/DefaultModal";
import { sharedHeaderStyle } from "../common/style_vanilla.css";
import { EntityTypeSurvey } from "../EntityTypeSurvey";
import { Question } from "../EntityTypeSurvey/types";
import { mainContentWrapper } from "../style_vanilla.css";
import { entitySurveySchema } from "./schemas";
import { EntitySurveyPageProps } from "./types";

type EntitySurveyFormValues = {
  updatedQuestions: Question[];
};

const EntitySurveyForm: React.FC<{
  questions: Question[];
  entityName: string;
  initialAmlEntityTypeId?: number | null;
  onBack: () => void;
}> = ({ questions, entityName, initialAmlEntityTypeId, onBack }) => {
  const { setFieldValue, touched, errors } = useFormContext<EntitySurveyFormValues>();

  return (
    <Flex flexDirection="column" gap="20px">
      <Text variant="regular-md">
        Additional information about {entityName} is needed in order to continue with this
        subscription.
      </Text>
      <EntityTypeSurvey
        questions={questions}
        entityName={entityName}
        initialAmlEntityTypeId={initialAmlEntityTypeId}
        onSurveyChange={(updatedQuestions) => {
          void setFieldValue("updatedQuestions", updatedQuestions);
        }}
      />
      <Flex flexDirection="column" gap="10px">
        {touched.updatedQuestions && errors.updatedQuestions && (
          <ErrorMessage>{errors.updatedQuestions.toString()}</ErrorMessage>
        )}
        <Flex flexDirection="row" gap="10px" justifyContent="flex-end">
          <Button variant="neutral" onClick={onBack}>
            Back
          </Button>
          <Button type="submit">Complete</Button>
        </Flex>
      </Flex>
    </Flex>
  );
};

export const EntitySurveyPage: React.FC<EntitySurveyPageProps> = ({
  questions,
  entityName,
  onCompleted,
  initialAmlEntityTypeId,
  onBack,
}: EntitySurveyPageProps) => {
  const [isBackModalVisible, setIsBackModalVisible] = useState(false);

  const handleBackAction = () => {
    setIsBackModalVisible(true);
  };

  const handleBackConfirm = () => {
    setIsBackModalVisible(false);
    onBack("Back button clicked");
  };

  const handleBackCancel = () => {
    setIsBackModalVisible(false);
  };

  return (
    <Card className={mainContentWrapper}>
      {entityName && (
        <Link
          to=""
          variant="muted"
          icon="arrow-left"
          iconPosition="left"
          align="left"
          onClick={(e) => {
            e.preventDefault();
            handleBackAction();
          }}
        >
          {entityName}
        </Link>
      )}
      <Text variant="h3" className={sharedHeaderStyle}>
        {`${entityName} Details`}
      </Text>
      <Form<EntitySurveyFormValues>
        initialValues={{
          updatedQuestions: questions.map((question) => ({ ...question, answer: "" })),
        }}
        yupValidationSchema={entitySurveySchema}
        submitOnlyChangedValues={false}
        onSubmit={async (values) => {
          await onCompleted(values);
        }}
      >
        <EntitySurveyForm
          questions={questions}
          entityName={entityName}
          onBack={handleBackAction}
          initialAmlEntityTypeId={initialAmlEntityTypeId}
        />
      </Form>
      <DefaultModal
        show={isBackModalVisible}
        title="Warning"
        body={"Are you sure you want to navigate back? Any unsaved information will be lost."}
        onClose={handleBackCancel}
        onConfirm={handleBackConfirm}
      />
    </Card>
  );
};
